import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AssetResultDTO, WorkTaskDTO2 } from '../api/api';
import AssetService from '../services/AssetService';
import NotificationService from '../services/NotificationService';
import TaskService from '../services/TaskService';
import TaskTypeService from '../services/TaskTypeService';
import { newTask, selectTasks, setAssetSearchStatus } from '../stateManagement/reducers/taskCreationReducer';
import { getWorkPermitTaskTypeId } from '../utils/constants';
import { getGuid } from '../utils/guidGenerating';
import { log } from '../utils/logging/log';
import { getWorkPermitMainTaskId, getWorkPermitTaskSeed } from '../utils/workPermitTaskSeed';

/**
 * Starts the create work task flow for a work permit (Arbejdstilladelse (KOS)) belonging to the main
 * task of the given task. Resolves to the id of the task being created, which the caller navigates
 * to, or undefined when it could not be started.
 */
const useCreateWorkPermitTask = () => {
  const dispatch = useDispatch();
  const tasks = useSelector(selectTasks);

  const createWorkPermitTask = useCallback(
    async (task: WorkTaskDTO2): Promise<string | undefined> => {
      if (tasks.length >= 3) {
        NotificationService.warning('Der kan højst være 3 åbne arbejdskort. Luk venligst en af de åbne arbejdskort.');
        return;
      }

      const workPermitTaskTypeId = getWorkPermitTaskTypeId();
      const mainTaskId = getWorkPermitMainTaskId(task);
      if (!task.id || !mainTaskId || isNaN(workPermitTaskTypeId)) return;

      try {
        const mainTask = mainTaskId === parseInt(task.id) ? task : await TaskService.getTask(mainTaskId);

        const taskTypes = await TaskTypeService.getActiveTaskTypes();
        const workPermitTaskType = taskTypes.taskTypeDTOs?.find((taskType) => taskType.id === workPermitTaskTypeId);
        if (!workPermitTaskType) {
          NotificationService.error('Kunne ikke finde opgavetypen for arbejdstilladelse');
          return;
        }

        const asset: AssetResultDTO =
          mainTask.assetId && mainTask.assetType
            ? (await AssetService.getAssets(mainTask.assetId, mainTask.assetType))[0] ?? {}
            : { location: mainTask.taskLocation, assetType: mainTask.assetType };

        const taskCreationId = getGuid();
        dispatch(
          newTask({
            taskCreationId,
            taskTypePickerInfo: taskTypes,
            ...getWorkPermitTaskSeed(mainTask, workPermitTaskType, asset)
          })
        );
        dispatch(setAssetSearchStatus({ taskCreationId, category: asset.assetType, locations: [asset] }));
        return taskCreationId;
      } catch (error) {
        log(error);
        NotificationService.error(`Kunne ikke oprette arbejdstilladelse: ${error}`, 5000);
      }
    },
    [dispatch, tasks.length]
  );

  return { createWorkPermitTask };
};

export default useCreateWorkPermitTask;
